import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { PaymentMethodActions } from 'Redux/thunks/payment-methods.thunks';

import SesamModal from 'Common/SesamModal/SesamModal';
import Button from 'Common/Button/Button';
import { CallToActionCard } from '../../../components/call-to-action-card/CallToActionCard.view';
import { CardList } from '../../../components/card-list/CardList';
import PromiseButton from '../../../components/promise-button';
import { usePaymentMethodPageCardsFactory } from 'src/hooks/usePaymentMethodPageCardsFactory';

import type { AppDispatch, RootState } from 'src/types/state.types';
import type { PaymentMethodAPIPayload } from 'src/types/paymentMethod.types';

import './index.css';
import { TestID } from 'src/testID';

const PaymentMethodsPage: React.FC<{
  paymentMethods: PaymentMethodAPIPayload[];
  reload: () => void;
  remove: (id: string) => Promise<void>;
  currentUserId: string;
  isPortalAdmin: boolean;
}> = ({ paymentMethods, reload, remove, currentUserId, isPortalAdmin }) => {
  const [deleting, setDeleting] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] =
    useState<PaymentMethodAPIPayload | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const showRemoveConfirmation = (pm: PaymentMethodAPIPayload) => {
    setPaymentMethodToDelete(pm);
    setShowDeleteConfirm(true);
  };

  const approveDelete = async () => {
    setDeleting(true);

    if (!paymentMethodToDelete) {
      setDeleting(false);
      return;
    }

    await remove(paymentMethodToDelete.id);

    setDeleting(false);
    setPaymentMethodToDelete(null);
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setPaymentMethodToDelete(null);
    setShowDeleteConfirm(false);
  };

  useEffect(() => {
    reload();
  }, [reload]);

  const data = usePaymentMethodPageCardsFactory({
    paymentMethods,
    showRemoveConfirmation,
    currentUserId,
    isPortalAdmin,
  });

  return (
    <main className="scrollArea">
      <h2 className="heading-section">Payment methods</h2>
      <CardList data={data} dataKey="id" id="payment-methods" renderer={CallToActionCard} />
      <SesamModal
        className="simple-dialog"
        isOpen={showDeleteConfirm}
        onRequestClose={cancelDelete}
        contentLabel="Confirmation"
      >
        <h2 className="heading-page">Please confirm</h2>
        <p>
          Do you want to delete the payment method &quot;
          {paymentMethodToDelete && paymentMethodToDelete.name}
          &quot;?
        </p>
        <div
          className="toolbar toolbar--right"
          data-testid={TestID.PaymentMethodDeleteConfirmModal}
        >
          <Button
            onClick={cancelDelete}
            disabled={deleting}
            data-testid={TestID.PaymentMethodDeleteModalCancelButton}
          >
            Cancel
          </Button>
          <PromiseButton
            onClick={approveDelete}
            pending="Deleting…"
            theme="danger"
            data-testid={TestID.PaymentMethodDeleteModalDeleteButton}
          >
            Yes, delete
          </PromiseButton>
        </div>
      </SesamModal>
    </main>
  );
};

function mapStateToProps(state: RootState) {
  return {
    paymentMethods: state.paymentMethods,
    currentUserId: state.user.user_id,
    isPortalAdmin: state.user.isPortalAdmin,
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return {
    reload: () => dispatch(PaymentMethodActions.loadAll()),
    remove: (id: string) => dispatch(PaymentMethodActions.remove(id)),
  };
}

const Connected = connect(mapStateToProps, mapDispatchToProps)(PaymentMethodsPage);

export { Connected as PaymentMethodsPage };
